import { useState } from 'react'
import { useCreateSupplier } from '@/hooks/useSuppliers'
import type { CreateSupplierData } from '@/types'

interface SupplierCreateFormProps {
  businessId: string
  onSuccess: (id: string) => void
  onCancel: () => void
}

export function SupplierCreateForm({ businessId, onSuccess, onCancel }: SupplierCreateFormProps) {
  const createMutation = useCreateSupplier()
  const [name, setName] = useState('')
  const [alternativeNames, setAlternativeNames] = useState<string[]>([])
  const [newAltName, setNewAltName] = useState('')

  const handleAddAltName = () => {
    const trimmed = newAltName.trim()
    if (trimmed && !alternativeNames.includes(trimmed)) {
      setAlternativeNames([...alternativeNames, trimmed])
      setNewAltName('')
    }
  }

  const handleRemoveAltName = (index: number) => {
    setAlternativeNames(alternativeNames.filter((_, i) => i !== index))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const data: CreateSupplierData = {
      business_id: businessId,
      name: name.trim(),
      alternative_names: alternativeNames.length > 0 ? alternativeNames : undefined,
    }

    createMutation.mutate(data, {
      onSuccess: (supplier) => {
        onSuccess(supplier.id)
      },
    })
  }

  return (
    <div className="p-6 space-y-6" data-testid="supplier-create">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
          data-testid="back-btn"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </button>
        <h2 className="text-xl font-semibold text-gray-900">Create Supplier</h2>
      </div>

      {/* Error message */}
      {createMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
          <p className="text-red-800">Failed to create supplier</p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="supplier-create-form">
          {/* Name field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Supplier Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              required
              maxLength={255}
              data-testid="name-input"
            />
          </div>

          {/* Alternative names */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alternative Names
            </label>
            <div className="space-y-2">
              {alternativeNames.map((altName, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md"
                >
                  <span className="flex-1 text-sm">{altName}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAltName(index)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`remove-alt-name-${index}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAltName}
                  onChange={(e) => setNewAltName(e.target.value)}
                  placeholder="Add alternative name..."
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddAltName()
                    }
                  }}
                  data-testid="new-alt-name-input"
                />
                <button
                  type="button"
                  onClick={handleAddAltName}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  data-testid="add-alt-name-btn"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              data-testid="create-btn"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
